<template>
  <svg
    class="control-icon"
    :style="sizeStyle"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    v-html="markup" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CONTROL_ICONS } from './controlIcons';

const props = withDefaults(
  defineProps<{
    /** A key of `CONTROL_ICONS` — the drawn set in `controlIcons.ts`. */
    name: string;
    /** Any CSS length; the icon is square, so this is both width and height. */
    size?: string;
  }>(),
  { size: '1em' },
);

/**
 * The map's markup for `name`. This is the ONE `v-html` in the sheet code and it
 * is safe by construction: `controlIcons.ts` is static, bundled source data
 * written by us — no user input, no network, no hub payload ever reaches it.
 * An unknown name renders an empty (still sized) svg instead of throwing, so a
 * typo'd name in a widget degrades to a blank slot, never a broken sheet.
 */
const markup = computed(() => CONTROL_ICONS[props.name] ?? '');

const sizeStyle = computed(() => ({ width: props.size, height: props.size }));
</script>

<style scoped>
/*
 * The stroke rules live here and nowhere else: every path in the set is a bare
 * `<path>/<circle>/<rect>` so it inherits them. 1.75 on a 24-grid is the
 * sketch's weight — it scales with the rendered size, so one rule holds at 13 px
 * (a value tile's label) and at 34 px (the dial's knob).
 */
.control-icon {
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linecap: round;
  stroke-linejoin: round;
}
</style>
